import { useMutation } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { ForwardArrow } from "#/components/ui";
import {
	JOB_STATUSES,
	jobStatusesFor,
	WILAYAS,
	WILAYAS_LATIN,
} from "#/lib/algeria";
import {
	leadAttribution,
	startForm,
	trackFieldComplete,
	trackFieldFocus,
	trackFormFailure,
	trackFormSubmitAttempt,
	trackFormValidationError,
	trackLeadConversion,
} from "#/lib/analytics";
import { useI18n } from "#/lib/i18n";

// Shared with the home page's registration form (src/components/home/).
export const PHONE_PATTERN = /^(0|\+213)[0-9\s]{8,13}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_CLASS =
	"w-full rounded-full border-0 bg-white px-6 py-4 text-base text-ink shadow-card outline-none ring-1 ring-line transition-shadow placeholder:text-muted/60 focus:ring-2 focus:ring-teal";

export function LeadForm({
	source,
	trainingId,
	trainingSlug,
}: {
	source: string;
	trainingId: string;
	/** Lets a training ask its own "situation" question — see jobStatusesFor. */
	trainingSlug?: string;
}) {
	const { t, locale } = useI18n();
	// Pulled from router context (not useTRPC()/<TRPCProvider>) because the
	// provider only wraps children in the browser — the server render uses an
	// in-process router proxy instead, to avoid the app self-fetching its own API.
	const { trpc } = useRouteContext({ from: "__root__" });
	// Stored value stays the canonical Arabic name; only the label is localized.
	const wilayaLabels = locale === "ar" ? WILAYAS : WILAYAS_LATIN;
	// A training with its own list (its labels travel with it); otherwise the
	// general-public statuses, whose labels are index-matched in the i18n dict.
	const ownStatuses = jobStatusesFor(trainingSlug);
	const jobStatusOptions = ownStatuses
		? ownStatuses.map((option) => ({
				value: option.value,
				label:
					locale === "ar"
						? option.value
						: locale === "fr"
							? option.fr
							: option.en,
			}))
		: JOB_STATUSES.map((value, i) => ({
				value,
				label: t.form.jobStatuses[i],
			}));
	const [fullName, setFullName] = useState("");
	const [phone, setPhone] = useState("");
	const [email, setEmail] = useState("");
	const [wilaya, setWilaya] = useState("");
	const [jobStatus, setJobStatus] = useState("");
	const [website, setWebsite] = useState(""); // honeypot
	const [error, setError] = useState<string | null>(null);

	const mutation = useMutation(
		trpc.leads.create.mutationOptions({
			onSuccess: () => {
				// The server answers "ok" to honeypot submissions without storing
				// anything, so the trap must be checked here too — otherwise a bot
				// would register as a conversion the database never received.
				if (website) return;
				trackLeadConversion({
					formId: source,
					wilaya,
					jobStatus,
					email: email.trim(),
					phone: phone.trim(),
				});
			},
			onError: (err) => {
				trackFormFailure(source, err.message);
			},
		}),
	);

	/** First focus anywhere in the form opens the funnel step. */
	function handleFocus(field: string) {
		startForm(source);
		trackFieldFocus(field);
	}

	function handleBlur(field: string, value: string) {
		trackFieldComplete(field, value.trim().length > 0);
	}

	/** Shows a validation message and reports which field blocked the submit. */
	function fail(field: string, message: string) {
		setError(message);
		trackFormValidationError(field, message);
	}

	function handleSubmit(event: React.FormEvent) {
		event.preventDefault();
		setError(null);
		// Fired before validation so "attempts vs. errors" is measurable.
		startForm(source);
		trackFormSubmitAttempt(source);

		if (fullName.trim().length < 2) {
			fail("fullName", t.form.errName);
			return;
		}
		if (!PHONE_PATTERN.test(phone.trim())) {
			fail("phone", t.form.errPhone);
			return;
		}
		if (!EMAIL_PATTERN.test(email.trim())) {
			fail("email", t.form.errEmail);
			return;
		}
		if (!wilaya) {
			fail("wilaya", t.form.errWilaya);
			return;
		}
		if (!jobStatus) {
			fail("jobStatus", t.form.errJob);
			return;
		}

		mutation.mutate({
			trainingId,
			fullName: fullName.trim(),
			phone: phone.trim(),
			email: email.trim(),
			wilaya,
			jobStatus,
			source,
			// First-touch campaign of the visit — stored on the lead so the sales
			// team sees which ad produced it.
			...leadAttribution(),
			website: website || undefined,
		});
	}

	if (mutation.isSuccess) {
		return (
			<div className="flex animate-fade-up flex-col items-center gap-3 rounded-3xl bg-teal-50 px-6 py-12 text-center ring-1 ring-teal/20">
				<span className="flex h-12 w-12 items-center justify-center rounded-full bg-teal text-white">
					<Check className="h-6 w-6" />
				</span>
				<h3 className="font-display text-xl font-bold text-navy">
					{t.form.successTitle}
				</h3>
				<p className="max-w-xs text-sm text-muted">{t.form.successText}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit} noValidate className="flex flex-col gap-3">
			<div className="flex flex-col gap-3 sm:flex-row">
				<input
					id="fullName"
					name="fullName"
					type="text"
					autoComplete="name"
					value={fullName}
					onChange={(e) => setFullName(e.target.value)}
					onFocus={() => handleFocus("fullName")}
					onBlur={(e) => handleBlur("fullName", e.target.value)}
					placeholder={t.form.fullName}
					aria-label={t.form.fullName}
					className={FIELD_CLASS}
				/>
				<input
					id="phone"
					name="phone"
					type="tel"
					inputMode="tel"
					autoComplete="tel"
					dir="ltr"
					value={phone}
					onChange={(e) => setPhone(e.target.value)}
					onFocus={() => handleFocus("phone")}
					onBlur={(e) => handleBlur("phone", e.target.value)}
					placeholder="0555 00 00 00"
					aria-label={t.form.phone}
					className={`${FIELD_CLASS} text-end`}
				/>
			</div>

			<input
				id="email"
				name="email"
				type="email"
				inputMode="email"
				autoComplete="email"
				dir="ltr"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				onFocus={() => handleFocus("email")}
				onBlur={(e) => handleBlur("email", e.target.value)}
				placeholder="you@email.com"
				aria-label={t.form.email}
				className={`${FIELD_CLASS} text-end`}
			/>

			<div className="flex flex-col gap-3 sm:flex-row">
				<select
					id="wilaya"
					name="wilaya"
					value={wilaya}
					onChange={(e) => {
						setWilaya(e.target.value);
						trackFieldComplete("wilaya", Boolean(e.target.value));
					}}
					onFocus={() => handleFocus("wilaya")}
					onBlur={(e) => handleBlur("wilaya", e.target.value)}
					aria-label={t.form.wilaya}
					className={`${FIELD_CLASS} appearance-none ${
						wilaya ? "text-ink" : "text-muted/60"
					}`}
				>
					<option value="" disabled>
						{t.form.wilayaPlaceholder}
					</option>
					{WILAYAS.map((w, i) => (
						<option key={w} value={w} className="text-ink">
							{i + 1} · {wilayaLabels[i]}
						</option>
					))}
				</select>
				<select
					id="jobStatus"
					name="jobStatus"
					value={jobStatus}
					onChange={(e) => {
						setJobStatus(e.target.value);
						trackFieldComplete("jobStatus", Boolean(e.target.value));
					}}
					onFocus={() => handleFocus("jobStatus")}
					onBlur={(e) => handleBlur("jobStatus", e.target.value)}
					aria-label={t.form.jobStatus}
					className={`${FIELD_CLASS} appearance-none ${
						jobStatus ? "text-ink" : "text-muted/60"
					}`}
				>
					<option value="" disabled>
						{t.form.jobStatusPlaceholder}
					</option>
					{jobStatusOptions.map((option) => (
						<option
							key={option.value}
							value={option.value}
							className="text-ink"
						>
							{option.label}
						</option>
					))}
				</select>
			</div>

			{/* honeypot field — hidden from real visitors, bots tend to fill every input */}
			<input
				type="text"
				name="website"
				value={website}
				onChange={(e) => setWebsite(e.target.value)}
				tabIndex={-1}
				autoComplete="off"
				aria-hidden="true"
				className="absolute left-[-9999px] h-0 w-0 opacity-0"
			/>

			{(error || mutation.isError) && (
				<p className="px-2 text-sm font-semibold text-rose-600">
					{error ?? t.form.errGeneric}
				</p>
			)}

			<button
				type="submit"
				disabled={mutation.isPending}
				className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-teal-700 px-8 py-4 text-base font-bold text-white shadow-glow transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-600 active:translate-y-0 active:scale-[0.985] disabled:pointer-events-none disabled:opacity-70"
			>
				{mutation.isPending ? (
					<Loader2 className="h-5 w-5 animate-spin" />
				) : (
					<>
						{t.form.submit}
						<ForwardArrow className="h-4 w-4 transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
					</>
				)}
			</button>

			<p className="px-2 text-center text-xs text-muted">{t.form.privacy}</p>
		</form>
	);
}
